import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppState } from 'src/app/store';
import { selectDomain, selectRoles } from 'src/app/store/selectors/auth.selectors';
import { canUseAiAssistant } from 'src/app/utils/ai-access';

export interface AiOverlayOpenOptions {
  /** The first message is the guided tour's sample question (not metered). */
  onboarding?: boolean;
}

interface AiOverlayState {
  isOpen: boolean;
  firstMessage?: string;
  onboarding?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AiOverlayService {
  private overlaySubject = new BehaviorSubject<AiOverlayState>({
    isOpen: false,
  });

  overlay$ = this.overlaySubject.asObservable();

  constructor(
    private store: Store<AppState>,
  ) {}

  open(firstMessage?: string, options: AiOverlayOpenOptions = {}) {
    this.store.select(selectDomain).pipe(take(1)).subscribe(() => {
      this.overlaySubject.next({
        isOpen: true,
        firstMessage,
        onboarding: !!options.onboarding,
      });
    });
  }

  get isOpen(): boolean {
    return this.overlaySubject.value.isOpen;
  }

  /**
   * For the /assistant links of articles and FAQs: a link written for every
   * domain must not open the assistant where the reader has no access to it.
   */
  openIfAvailable() {
    combineLatest([this.store.select(selectDomain), this.store.select(selectRoles)])
      .pipe(take(1))
      .subscribe(([domain, roles]) => {
        if (canUseAiAssistant(domain, roles)) {
          this.open();
        }
      });
  }

  close() {
    this.overlaySubject.next({
      isOpen: false,
    });
  }
}
